// ExternalMorningCallSection — compliance mirror flag for External orders
// that arrived after 5 PM. Same component used by both OrderDetailPage
// (/order/:id) and SchedulingOrderDetail (/scheduling/order/:id).
// Apr 2026: extracted from App.js so it can be reused in /scheduling/order/:id.
// Behaviour, endpoint, button text, conditions are IDENTICAL to the original.
import React from "react";
import axios from "axios";
import { Phone, CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ExternalMorningCallSection = ({ order, orderId, onChange }) => {
  const required = (order.external_morning_call_required || '').toLowerCase() === 'yes';
  const completed = !!order.external_morning_call_completed_at;
  const [flagging, setFlagging] = React.useState(false);
  const [showLog, setShowLog] = React.useState(false);
  const [logResult, setLogResult] = React.useState('');
  const [logNotes, setLogNotes] = React.useState('');
  const [err, setErr] = React.useState('');

  const flag = async (value) => {
    setFlagging(true); setErr('');
    try {
      await axios.patch(`${API}/orders/${orderId}/external-morning-call`, { required: value });
      onChange?.({
        external_morning_call_required: value ? 'yes' : 'no',
        ...(value ? { external_morning_call_completed_at: '', external_morning_call_completed_by: '', external_morning_call_result: '', external_morning_call_notes: '' } : {}),
      });
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to update flag');
    } finally { setFlagging(false); }
  };

  const logCall = async () => {
    if (!logResult.trim()) { setErr('Select a result'); return; }
    setFlagging(true); setErr('');
    try {
      await axios.post(`${API}/orders/${orderId}/external-morning-call/log`, { result: logResult, notes: logNotes.trim() });
      const now = new Date().toISOString();
      onChange?.({
        external_morning_call_completed_at: now,
        external_morning_call_result: logResult,
        external_morning_call_notes: logNotes.trim(),
      });
      setShowLog(false); setLogResult(''); setLogNotes('');
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to log call');
    } finally { setFlagging(false); }
  };

  if (!required && !completed) {
    return (
      <div className="mb-6" data-testid="external-morning-call-section">
        <button
          onClick={() => flag(true)}
          disabled={flagging}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-[#FFF7ED] text-[#B45309] border border-[#FDBA74]/60 rounded-lg text-xs font-semibold transition-colors"
          data-testid="flag-morning-call-btn"
        >
          <Phone className="h-3.5 w-3.5" />
          Send to External Morning Calls Queue
        </button>
      </div>
    );
  }

  return (
    <div className="mb-6" data-testid="external-morning-call-section">
      {required && !completed && (
        <div className="bg-[#FFF7ED] border border-[#FDBA74]/60 rounded-lg px-3 py-2 flex items-center justify-between gap-2 flex-wrap" data-testid="morning-call-pending">
          <div className="flex items-center gap-2 min-w-0">
            <Phone className="h-3.5 w-3.5 text-[#B45309] shrink-0" />
            <div className="text-xs min-w-0">
              <span className="font-bold text-[#9A3412]">External Morning Call pending.</span>
              {order.external_morning_call_required_reason && <span className="ml-1 text-[#9A3412]/80">{order.external_morning_call_required_reason}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowLog(true)} disabled={flagging}
              className="px-3 py-1 bg-[#B45309] hover:bg-[#92400E] text-white text-xs font-bold rounded"
              data-testid="log-morning-call-btn">
              Log Call
            </button>
            <button onClick={() => flag(false)} disabled={flagging}
              className="px-2 py-1 text-[10px] font-semibold text-[#9A3412] hover:bg-[#FDBA74]/30 rounded"
              data-testid="unflag-morning-call-btn">
              Remove flag
            </button>
          </div>
        </div>
      )}
      {completed && (
        <div className="bg-[#EFF5ED] border border-[#A7C4A0]/60 rounded-lg px-3 py-2 flex items-center gap-2 text-xs" data-testid="morning-call-completed">
          <CheckCircle className="h-3.5 w-3.5 text-[#4A7A56] shrink-0" />
          <span className="font-bold text-[#4A7A56]">Morning Call logged.</span>
          <span className="text-[#4A7A56]/80">{order.external_morning_call_result}</span>
          {order.external_morning_call_completed_by && <span className="text-gray-500">by {order.external_morning_call_completed_by}</span>}
          {order.external_morning_call_completed_at && <span className="text-gray-400">· {new Date(order.external_morning_call_completed_at).toLocaleString()}</span>}
        </div>
      )}

      {showLog && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setShowLog(false)}>
          <div className="bg-white rounded-xl border border-gray-200 p-5 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()} data-testid="log-morning-call-modal">
            <h3 className="text-base font-bold text-gray-800 mb-1">Log Morning Call</h3>
            <p className="text-gray-500 text-xs mb-4">Order #{order.order_number}</p>
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Result *</label>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {['Spoke with PH', 'Left voicemail', 'No answer', 'Wrong number'].map(opt => (
                <button key={opt} onClick={() => setLogResult(opt)}
                  className={`px-3 py-2 rounded-lg border text-xs font-semibold transition-colors ${logResult === opt ? 'bg-[#B45309] text-white border-[#B45309]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}
                  data-testid={`result-${opt.replace(/\s/g,'-').toLowerCase()}`}>
                  {opt}
                </button>
              ))}
            </div>
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Notes (optional)</label>
            <textarea value={logNotes} onChange={e => setLogNotes(e.target.value)}
              placeholder="Optional notes about the call..."
              className="w-full px-3 py-2 border border-gray-300 rounded text-xs focus:outline-none focus:border-[#B45309] mb-3"
              rows={3}
              data-testid="morning-call-notes-input"
            />
            {err && <p className="text-xs text-[#C07070] mb-2">{err}</p>}
            <div className="flex items-center gap-2 justify-end">
              <button onClick={() => { setShowLog(false); setErr(''); }}
                className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded">Cancel</button>
              <button onClick={logCall} disabled={flagging || !logResult}
                className="px-3 py-1.5 bg-[#B45309] hover:bg-[#92400E] disabled:bg-gray-300 text-white text-xs font-bold rounded"
                data-testid="submit-morning-call-log-btn">
                {flagging ? 'Saving...' : 'Save Call Log'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExternalMorningCallSection;
